import { useMemo } from "react";
import { useCoverage } from "./useCoverage";
import type { TreatmentLimit } from "./useCoverage";
import { useLocale } from "@src/packages/i18n/useLocale";
import type { Locale } from "@src/packages/i18n/locale";

export type CoverageCardState = "empty" | "pending" | "verified";

export interface CoverageFinancials {
  cardState: CoverageCardState;
  isLoading: boolean;

  // Insurance info
  carrierName: string | null;
  planName: string | null;

  // Financial fields (formatted strings, null when unknown)
  deductible: string | null;
  outOfPocketMax: string | null;
  coinsurancePercent: string | null;
  annualMax: string | null;
  lifetimeMax: string | null;
  copay: string | null;

  // Coverage booleans
  hasFertilityCoverage: boolean | null;
  treatmentsCovered: string[];
  medsCovered: boolean | null;
  diagnosticsCovered: boolean | null;
  cryostorageCovered: boolean | null;

  // Treatment limits (humanized)
  treatmentLimits: string[];
}

const NUMBER_LOCALE_TAG: Record<Locale, string> = {
  en: "en-US",
  fr: "fr-FR",
};

function formatCents(cents: number | null, locale: Locale): string | null {
  if (cents == null) return null;
  const dollars = cents / 100;
  const tag = NUMBER_LOCALE_TAG[locale];
  if (dollars >= 1000) {
    return `$${dollars.toLocaleString(tag, { maximumFractionDigits: 0 })}`;
  }
  return `$${dollars.toLocaleString(tag, { maximumFractionDigits: 0 })}`;
}

function formatPercent(pct: number | null): string | null {
  if (pct == null) return null;
  return `${pct}%`;
}

const TREATMENT_LABELS: Record<string, string> = {
  ivf: "IVF",
  iui: "IUI",
  egg_freezing: "Egg Freezing",
  embryo_freezing: "Embryo Freezing",
  sperm_freezing: "Sperm Freezing",
  fet: "Frozen Transfer",
  icsi: "ICSI",
  pgta: "PGT-A",
  donor_egg: "Donor Egg",
  donor_sperm: "Donor Sperm",
  surrogacy: "Surrogacy",
};

function humanizeTreatment(raw: string): string {
  const lower = raw.toLowerCase().replace(/[\s-]+/g, "_");
  return (
    TREATMENT_LABELS[lower] ??
    raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function humanizePeriod(period: TreatmentLimit["limitPeriod"]): string {
  switch (period) {
    case "lifetime":
      return "per lifetime";
    case "calendar_year":
      return "per year";
    case "plan_year":
      return "per plan year";
    default:
      return "";
  }
}

function humanizeTreatmentLimit(limit: TreatmentLimit, locale: Locale): string {
  const treatment = humanizeTreatment(limit.treatmentType);
  const parts: string[] = [treatment + ":"];

  if (limit.cycleLimit != null) {
    parts.push(
      `${limit.cycleLimit} cycle${limit.cycleLimit !== 1 ? "s" : ""} ${humanizePeriod(limit.limitPeriod)}`,
    );
  } else if (limit.maxAmountCents != null) {
    parts.push(
      `${formatCents(limit.maxAmountCents, locale)} ${humanizePeriod(limit.limitPeriod)}`,
    );
  }

  return parts.join(" ");
}

export function useCoverageFinancials(): CoverageFinancials {
  const {
    isLoading,
    hasInsurance,
    primaryPolicy,
    fertilityCoverage,
    verificationStatus,
  } = useCoverage();
  const { locale } = useLocale();

  return useMemo(() => {
    // Determine card state
    let cardState: CoverageCardState = "empty";
    if (!hasInsurance) {
      cardState = "empty";
    } else if (verificationStatus === "verified" && fertilityCoverage) {
      cardState = "verified";
    } else {
      // Has insurance but no verified fertility data yet
      cardState = "pending";
    }

    // Insurance info
    const carrierName = primaryPolicy?.insuranceCarrier ?? null;
    const planName = primaryPolicy?.planType ?? primaryPolicy?.planName ?? null;

    // Financial fields
    const fc = fertilityCoverage;
    const deductible = fc ? formatCents(fc.deductibleCents, locale) : null;
    const outOfPocketMax = fc
      ? formatCents(fc.outOfPocketMaxCents, locale)
      : null;
    const coinsurancePercent = fc ? formatPercent(fc.coinsurancePercent) : null;
    const annualMax = fc ? formatCents(fc.annualMaxCents, locale) : null;
    const lifetimeMax = fc ? formatCents(fc.lifetimeMaxCents, locale) : null;
    const copay = fc ? formatCents(fc.copayCents, locale) : null;

    // Coverage booleans
    const hasFertilityCoverage = fc?.hasCoverage ?? null;
    const treatmentsCovered = (fc?.treatmentsCovered ?? []).map(
      humanizeTreatment,
    );
    const medsCovered = fc?.medsCovered ?? null;
    const diagnosticsCovered = fc?.diagnosticsCovered ?? null;
    const cryostorageCovered = fc?.cryostorageCovered ?? null;

    // Treatment limits
    const treatmentLimits = (fc?.treatmentLimits ?? []).map((limit) =>
      humanizeTreatmentLimit(limit, locale),
    );

    return {
      cardState,
      isLoading,
      carrierName,
      planName,
      deductible,
      outOfPocketMax,
      coinsurancePercent,
      annualMax,
      lifetimeMax,
      copay,
      hasFertilityCoverage,
      treatmentsCovered,
      medsCovered,
      diagnosticsCovered,
      cryostorageCovered,
      treatmentLimits,
    };
  }, [
    isLoading,
    hasInsurance,
    primaryPolicy,
    fertilityCoverage,
    verificationStatus,
    locale,
  ]);
}
